import React, { useState, useEffect } from 'react';
import {
  HiUser,
  HiEnvelope,
  HiLockClosed,
  HiShieldCheck,
  HiTrash,
  HiCog,
  HiClock,
  HiBell,
  HiCreditCard,
  HiCloudArrowUp,
} from 'react-icons/hi2';
import ProfileSettings from '../../components/Settings/ProfileSettingsEnhanced';
import EmailSettings from '../../components/Settings/EmailSettings';
import PasswordSettings from '../../components/Settings/PasswordSettings';
import TwoFactorSettings from '../../components/Settings/TwoFactorSettings';
import DeleteAccountSettings from '../../components/Settings/DeleteAccountSettings';
import SecurityLogs from '../../components/Settings/SecurityLogs';
import UploadDocuments from '../../components/Settings/UploadDocuments';
import SystemNotifications from '../../components/Settings/SystemNotifications';
import SubscriptionBilling from '../../components/Settings/SubscriptionBilling';
import { useUserRole } from '../../context/UserRoleContext';
import { getClientOtpSettings } from '../../services/settingsService';

const tabs = [
  { id: 'profile', label: 'Profile', icon: HiUser, color: 'blue' },
  { id: 'email', label: 'Email', icon: HiEnvelope, color: 'blue' },
  { id: 'password', label: 'Password', icon: HiLockClosed, color: 'blue' },
  { id: '2fa', label: 'Two-Factor Auth', icon: HiShieldCheck, color: 'green' },
  { id: 'security', label: 'Security Logs', icon: HiClock, color: 'blue' },
  { id: 'notifications', label: 'Notifications', icon: HiBell, color: 'blue' },
  { id: 'billing', label: 'Billing', icon: HiCreditCard, color: 'blue' },
  { id: 'documents', label: 'Documents', icon: HiCloudArrowUp, color: 'blue' },
  { id: 'delete', label: 'Delete Account', icon: HiTrash, color: 'red' },
];

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const { userRole } = useUserRole();
  const [clientOtpStatus, setClientOtpStatus] = useState({
    loading: true,
    enabled: false,
    expiryMinutes: 10,
  });

  useEffect(() => {
    let isMounted = true;
    const loadOtp = async () => {
      if (userRole !== 'client') {
        if (isMounted) {
          setClientOtpStatus(prev => ({ ...prev, loading: false }));
        }
        return;
      }
      try {
        const data = await getClientOtpSettings();
        if (!isMounted) return;
        setClientOtpStatus({
          loading: false,
          enabled: !!data.clientOtpEnabled,
          expiryMinutes: data.expiryMinutes ?? 10,
        });
      } catch {
        if (isMounted) {
          setClientOtpStatus(prev => ({ ...prev, loading: false }));
        }
      }
    };
    loadOtp();
    return () => {
      isMounted = false;
    };
  }, [userRole]);

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileSettings />;
      case 'email':
        return <EmailSettings />;
      case 'password':
        return <PasswordSettings />;
      case '2fa':
        return (
          <div className="space-y-6">
            <TwoFactorSettings />
            {userRole === 'client' && (
              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">
                    <HiShieldCheck className="h-4 w-4" />
                  </div>
                  <div className="flex-1 text-sm text-gray-800">
                    <p className="font-semibold text-black">Client OTP for Logins</p>
                    {clientOtpStatus.loading ? (
                      <p className="mt-1 text-xs text-gray-500">Checking OTP status…</p>
                    ) : (
                      <>
                        <p className="mt-1 text-xs text-gray-600">
                          {clientOtpStatus.enabled ? (
                            <>
                              A one-time password (OTP) will be required each time you sign in, in
                              addition to your email and password. OTP codes are sent to your email
                              and expire after approximately {clientOtpStatus.expiryMinutes}{' '}
                              minutes.
                            </>
                          ) : (
                            <>
                              OTP for client logins is currently <strong>disabled</strong> by your
                              program administrators. You will sign in using your primary
                              credentials and any personal 2FA you enable above.
                            </>
                          )}
                        </p>
                        <p className="mt-2 text-[11px] text-gray-500">
                          This client OTP setting is managed centrally by administrators and is
                          separate from your personal Two-Factor Authentication (2FA) setup.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case 'security':
        return <SecurityLogs />;
      case 'notifications':
        return <SystemNotifications />;
      case 'billing':
        return <SubscriptionBilling />;
      case 'documents':
        return <UploadDocuments />;
      case 'delete':
        return <DeleteAccountSettings />;
      default:
        return <ProfileSettings />;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white">
              <HiCog className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage your account settings and preferences
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-3">
            <nav className="space-y-1 rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
              {tabs.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`group relative w-full rounded-lg px-4 py-3 text-left transition-colors ${
                      isActive
                        ? tab.color === 'red'
                          ? 'bg-red-600 text-white'
                          : tab.color === 'green'
                            ? 'bg-green-600 text-white'
                            : 'bg-blue-600 text-white'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5 flex-shrink-0" />
                      <span className="font-medium">{tab.label}</span>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-9">
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="p-6">{renderContent()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
